import { Inject, Injectable } from '@angular/core';
import { AuthUrlService, ServerAuthUrlPipe } from 'auth';
import { ENVIRONMENT } from 'shared';

/**
 * The one place that converts between the two forms an upload URL takes.
 *
 * STORED — `/api/media/upload/12/…/x.png`, server-relative. This is what
 * `CommunityAttachmentStore` returns and what the backend's sanitizer requires:
 * `CommunitySanitizer.own_upload?` tests `src.start_with?('/api/media/upload/')`,
 * so an `<img>` saved with any other src is dropped on the way in. It is also the
 * only form that survives a move between environments.
 *
 * DISPLAY — absolute against `environment.serverUrl`, plus the auth token on the
 * hybrid builds (an `<img>` carries no Authorization header). The stored form
 * cannot be rendered directly: it resolves against the PAGE origin, which is the
 * dev server on :4300, or the app bundle on mobile — not the API.
 *
 * Both directions are needed because the editor shows the picture while it is
 * being written: it renders DISPLAY and must hand STORED back on save.
 * `toStored(toDisplay(x)) === x`, so re-binding an emitted value is stable.
 */
@Injectable({ providedIn: 'root' })
export class CommunityUploadUrlService {
  /** The canonical prefix the backend stores and its sanitizer insists on. */
  static readonly STORED_PREFIX = '/api/media/upload/';
  /** The part after the API path, common to both forms — what the inverse keys off. */
  private static readonly MARKER = '/media/upload/';

  private readonly serverAuthUrl: ServerAuthUrlPipe;
  /** Path part of `serverUrl`, e.g. "/api" — it already contains the "/api" the stored form repeats. */
  private readonly serverPath: string;

  constructor(authUrlService: AuthUrlService, @Inject(ENVIRONMENT) private environment: any) {
    this.serverAuthUrl = new ServerAuthUrlPipe(authUrlService, environment);
    let path = '';
    try {
      path = new URL(String(environment?.serverUrl ?? ''), 'http://localhost').pathname.replace(/\/$/, '');
    } catch {
      path = '';
    }
    this.serverPath = path;
  }

  /** STORED (or an already-prefixed path) → DISPLAY. */
  toDisplay(src: string | null | undefined): string {
    if (!src) return '';
    let normalized = src;
    // serverUrl ends in "/api" and the stored form starts with "/api": strip one.
    if (this.serverPath && normalized.startsWith(`${this.serverPath}/`)) {
      normalized = normalized.slice(this.serverPath.length);
    }
    return this.serverAuthUrl.transform(normalized);
  }

  /** DISPLAY → STORED. Leaves anything that is not one of our uploads untouched. */
  toStored(src: string | null | undefined): string {
    if (!src) return '';
    if (src.startsWith(CommunityUploadUrlService.STORED_PREFIX)) return src;
    if (!src.startsWith('/') && !src.startsWith(String(this.environment?.serverUrl ?? '\0'))) return src;

    const index = src.indexOf(CommunityUploadUrlService.MARKER);
    if (index < 0) return src;

    let rest = src.slice(index + CommunityUploadUrlService.MARKER.length);
    const query = rest.search(/[?#]/);
    if (query >= 0) rest = rest.slice(0, query);
    return `${CommunityUploadUrlService.STORED_PREFIX}${rest}`;
  }

  /** Rewrites every `<img src>` of a body for rendering/editing. */
  htmlToDisplay(html: string | null | undefined): string {
    return this.mapImageSources(html, src => (this.isUpload(src) ? this.toDisplay(src) : src));
  }

  /** The inverse, for the value the editor hands back. */
  htmlToStored(html: string | null | undefined): string {
    return this.mapImageSources(html, src => this.toStored(src));
  }

  private isUpload(src: string): boolean {
    return src.startsWith(CommunityUploadUrlService.STORED_PREFIX) ||
      (!!this.serverPath && src.startsWith(`${this.serverPath}${CommunityUploadUrlService.MARKER}`));
  }

  /**
   * A plain regex, not an HTML parser: the body already went through the
   * backend's sanitizer (a handful of allowed tags survive it), and a DOM
   * round-trip inside the editor would fight Quill over the markup.
   */
  private mapImageSources(html: string | null | undefined, map: (src: string) => string): string {
    if (!html) return '';
    return html.replace(/(<img\b[^>]*\ssrc=")([^"]+)(")/gi, (match, pre, src, post) => {
      const mapped = map(src);
      return mapped === src ? match : pre + mapped + post;
    });
  }
}
